import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import STITCH_TYPES from './stitchTypes.js';

const Legend = ({ usedStitches }) => {
  const canvasRefs = useRef({});

  // Helper function to draw SVG path on canvas (copied from chartUtils)
  const drawSvgPathOnCanvas = (ctx, path, x, y, width, height, viewBox, stroke, strokeWidth, fill) => {
    if (!path) return;
    
    const [vbX, vbY, vbW, vbH] = viewBox.split(' ').map(Number);
    const scaleX = width / vbW;
    const scaleY = height / vbH;
    
    ctx.save();
    ctx.translate(x - width/2, y - height/2);
    ctx.scale(scaleX, scaleY);
    ctx.translate(-vbX, -vbY);
    
    const path2D = new Path2D(path);
    
    if (fill && fill !== 'none') {
      ctx.fillStyle = fill;
      ctx.fill(path2D);
    }
    
    if (stroke && stroke !== 'none') {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = strokeWidth || 1;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(path2D);
    }
    
    ctx.restore();
  };

  // Effect to draw symbols when component mounts or updates
  useEffect(() => {
    // Draw stitch symbol on canvas
    const drawStitchSymbol = (stitchType) => {
      const canvas = canvasRefs.current[stitchType];
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const baseSize = 18; // Base size of the legend symbol
      const stitch = STITCH_TYPES[stitchType];
      const stitchWidth = stitch?.width || 1;
      
      // Calculate canvas dimensions based on stitch width
      const canvasWidth = baseSize * stitchWidth;
      const canvasHeight = baseSize;
      
      // Get device pixel ratio for high DPI displays
      const dpr = window.devicePixelRatio || 1;
      
      // Set canvas size accounting for DPI and stitch width
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;
      
      // Scale context for DPI
      ctx.scale(dpr, dpr);
      
      // Clear canvas
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      
      // Set white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // Draw border
      ctx.strokeStyle = '#999';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(0.5, 0.5, canvasWidth - 1, canvasHeight - 1);

      if (stitch && stitch.symbol && stitch.symbol.path) {
        const symbol = stitch.symbol;
        const centerX = canvasWidth / 2;
        const centerY = canvasHeight / 2;
        const symbolWidth = canvasWidth * 0.8; // Use 80% of canvas width for symbol
        const symbolHeight = canvasHeight * 0.8; // Use 80% of canvas height for symbol
        
        // Draw the stitch symbol
        drawSvgPathOnCanvas(
          ctx,
          symbol.path,
          centerX,
          centerY,
          symbolWidth,
          symbolHeight,
          symbol.viewBox || '0 0 24 24',
          symbol.stroke || '#333',
          symbol.strokeWidth || 1,
          symbol.fill || 'none'
        );
      }
    };

    usedStitches.forEach(stitchType => {
      drawStitchSymbol(stitchType);
    });
  }, [usedStitches]);

  if (!usedStitches || usedStitches.length === 0) {
    return null;
  }

  return (
    <Box sx={{
      padding: 2,
      backgroundColor: '#ffffff',
      border: '1px solid #ddd',
      borderRadius: 1,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      minWidth: 200,
      maxWidth: 300
    }}>
      <Typography variant="h6" sx={{
        margin: 0,
        marginBottom: 1.5,
        fontSize: 16,
        fontWeight: 'normal',
        color: '#333',
        borderBottom: '1px solid #eee',
        paddingBottom: 1
      }}>
        Stitch Legend
      </Typography>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5
      }}>
        {usedStitches
          .sort((a, b) => {
            // Sort by the order they appear in STITCH_TYPES
            const keysArray = Object.keys(STITCH_TYPES);
            const indexA = keysArray.indexOf(a);
            const indexB = keysArray.indexOf(b);
            return indexA - indexB;
          })
          .map(stitchType => {
          const stitch = STITCH_TYPES[stitchType];
          if (!stitch) return null;

          return (
            <Box key={stitchType} sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              alignItems: 'center',
              gap: 1,
              padding: '2px 0'
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                minWidth: 'fit-content'
              }}>
                <canvas
                  ref={el => canvasRefs.current[stitchType] = el}
                  style={{
                    flexShrink: 0
                  }}
                />
              </Box>
              <Box sx={{
                flex: 1,
                minWidth: 0
              }}>
                <Typography variant="body2" sx={{
                  fontSize: 14,
                  fontWeight: 'normal',
                  color: '#333',
                  marginBottom: 0.25
                }}>
                  {stitch.description || stitchType}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Legend;